
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "../formatPrice";

export type Feature = {
  text: string;
  description?: string;
};

export type Plan = {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  features?: (string | Feature)[];
  is_recommended?: boolean;
  is_disabled?: boolean;
  disabled_text?: string;
};

export function usePricingPlans(currency: string) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('subscription_plans')
          .select(`
            *,
            plan_feature_mapping(
              plan_features(id, name, description)
            ),
            plan_currency_prices(price, currency)
          `)
          .eq('is_active', true)
          .order('price');
          
        if (error) {
          console.error("Error fetching subscription plans:", error);
          setError(error.message);
          return;
        }
        
        console.log("Fetched plans with features and currency prices:", data);

        if (!data || data.length === 0) {
          console.log("No active plans found, using default plans");
          
          const defaultPlans = [
            {
              id: 'free-plan',
              name: 'Free',
              description: 'Basic features for getting started',
              price: 0,
              currency: currency,
              features: [
                { text: 'Up to 5 services', description: 'Create and manage up to 5 different services' },
                { text: 'Access to basic templates', description: 'Use our collection of basic service templates' },
                { text: 'Email support', description: 'Get help via email within 48 hours' }
              ],
              is_recommended: false,
              is_disabled: false
            },
            {
              id: 'hustle-plan',
              name: 'Hustle',
              description: 'Perfect for growing businesses',
              price: 1500,
              currency: currency,
              features: [
                { text: 'Unlimited services', description: 'Create and manage unlimited services' },
                { text: 'Access to all templates', description: 'Use our entire collection of service templates' },
                { text: 'Priority email support', description: 'Get priority help via email within 24 hours' },
                { text: 'Customized branding', description: 'Add your own branding elements to your service page' }
              ],
              is_recommended: true,
              is_disabled: false
            },
            {
              id: 'growth-plan',
              name: 'Growth',
              description: 'Advanced features for established businesses',
              price: 3000,
              currency: currency,
              features: [
                { text: 'Unlimited services', description: 'Create and manage unlimited services' },
                { text: 'Premium templates', description: 'Access to exclusive premium service templates' },
                { text: '24/7 priority support', description: 'Get priority support anytime, day or night' },
                { text: 'Advanced analytics', description: 'Detailed insights about your business performance' }
              ],
              is_recommended: false,
              is_disabled: true,
              disabled_text: 'Coming Soon'
            }
          ];
          
          setPlans(defaultPlans);
        } else {
          const plansWithFeatures = data.map(plan => {
            const currencyPrice = plan.plan_currency_prices?.find(cp => cp.currency === currency);
            
            const priceForCurrency = currencyPrice ? currencyPrice.price : plan.price;
            
            const features = plan.plan_feature_mapping
              ?.filter(mapping => mapping.plan_features)
              ?.map(mapping => ({
                text: mapping.plan_features.name,
                description: mapping.plan_features.description
              })) || [];
              
            return {
              id: plan.id,
              name: plan.name,
              description: plan.description,
              price: priceForCurrency,
              currency: currency,
              features: features.length > 0 ? features : 
                (plan.description ? 
                  plan.description.split('\n').filter(line => line.trim().length > 0).map(text => ({ text })) : 
                  [{ text: 'No features specified' }]),
              is_recommended: plan.is_recommended,
              is_disabled: plan.is_disabled,
              disabled_text: plan.disabled_text
            };
          });
          
          setPlans(plansWithFeatures);
        }
      } catch (err: any) {
        console.error("Exception in fetchPlans:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPlans();
  }, [currency]);

  return { plans, isLoading, error };
}
